import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

const AFTER_IMG =
  "https://images.unsplash.com/photo-1497366412874-3415097a27e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjbGVhbiUyMG9mZmljZSUyMGludGVyaW9yJTIwYXJjaGl0ZWN0dXJlJTIwbHV4dXJ5fGVufDB8fHx8MTc5MjQ5OTEwN3ww&ixlib=rb-4.1.0&q=85";

const GRID = [
  {
    src: "/camion.png",
    label: "Notre véhicule d'intervention — Gap et alentours",
    real: true,
  },
  {
    src: "https://images.unsplash.com/photo-1605437241278-c1806d14a4d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHxfHxjYXIlMjBpbnRlcmlvciUyMGRldGFpbGluZyUyMHByaXN0aW5lJTIwbGVhdGhlciUyMHNlYXRzfGVufDB8fHx8MTc5MzE4ODgzOHww&ixlib=rb-4.1.0&q=80&w=900",
    label: "Nettoyage intérieur de véhicule — visuel illustratif",
  },
  {
    src: "https://images.unsplash.com/photo-1721395286594-8913b06056eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxyZW5vdmF0ZWQlMjBlbXB0eSUyMG1vZGVybiUyBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3OTMxODg4NDF8MA&ixlib=rb-4.1.0&q=80&w=900",
    label: "Remise en propreté après travaux — visuel illustratif",
  },
  {
    src: "https://images.pexels.com/photos/245219/pexels-photo-245219.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    label: "Entretien de locaux professionnels — visuel illustratif",
  },
];

const BeforeAfter = () => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid="gallery-before-after-slider"
      className="relative w-full aspect-[16/9] rounded-3xl overflow-hidden border border-white/10 select-none cursor-ew-resize"
      style={{ touchAction: "pan-y" }}
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <img src={AFTER_IMG} alt="Démonstration après nettoyage" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={AFTER_IMG}
          alt="Démonstration avant nettoyage"
          className="absolute inset-0 w-full h-full object-cover grayscale brightness-[0.45] contrast-125"
          draggable={false}
        />
        <span className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.25em] font-bold text-white/80 bg-black/50 backdrop-blur rounded-full px-3 py-1.5">
          Avant
        </span>
      </div>
      <span className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.25em] font-bold text-[#0A0D14] bg-amber-300 rounded-full px-3 py-1.5">
        Après
      </span>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -left-px w-0.5 bg-amber-300 shadow-[0_0_20px_rgba(242,193,78,0.8)]" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-[#0A0D14] border-2 border-amber-300 flex items-center justify-center shadow-[0_0_25px_rgba(242,193,78,0.5)]">
          <MoveHorizontal className="w-5 h-5 text-amber-300" />
        </div>
      </div>
    </div>
  );
};

export const Gallery = () => (
  <section id="galerie" data-testid="gallery-section" className="py-28 sm:py-36 bg-[#0D1220]">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6"
      >
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold mb-4">Galerie</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            La preuve <span className="text-amber-300">par l'image</span>
          </h2>
        </div>
        <p className="text-sm text-slate-400 max-w-sm">
          Faites glisser le curseur pour comparer avant / après, à la souris ou au doigt.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8 }}
      >
        <BeforeAfter />
        <p data-testid="gallery-slider-note" className="mt-3 text-xs text-slate-500 italic">
          Comparatif de démonstration (visuel illustratif) — les photos de nos interventions arrivent bientôt.
        </p>
      </motion.div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {GRID.map((g, i) => (
          <motion.figure
            key={g.label}
            data-testid={`gallery-photo-${i}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: i * 0.08 }}
            className="group relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10"
          >
            <img
              src={g.src}
              alt={g.label}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <figcaption className="absolute bottom-4 left-4 right-4 text-xs font-semibold text-white translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
              {g.label}
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </div>
  </section>
);
